// ─────────────────────────────────────────────────────────────
// ImageLibraryTab — unified single-scroll view of the user's
// own images and the shared library, with live keyword search.
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { imageApi } from "../../api/client";
import type {
  AuthUser,
  ImageListFilters,
  LibraryImage,
  UserImage,
} from "../../api/client";
import { FullscreenImageModal, UploadImageModal } from "./modals";
import { MetadataHoverSummary, MetadataViewerDialog } from "./MetadataChip";
import { DerivedTagChips } from "./DerivedTagChips";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";

// ── Auth user helper ──────────────────────────────────────────

function getAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem("auth_user");
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

// ── Type badge chip ───────────────────────────────────────────
//
// The legacy `image_type` TypeBadge ("SEM" / "TEM" / "SDB" / "OPTICAL")
// used to render next to the title on every card. As of 2026-05-05 the
// modality is auto-derived from FEI metadata server-side and surfaced
// via `DerivedTagChips`, so the legacy badge was duplicate noise and
// has been removed from the card render path. The component itself is
// no longer used anywhere — kept out of this file to keep things tidy.

// ── User-tag display filter ───────────────────────────────────
//
// On the card grid the derived modality chip already calls out
// SEM / TEM / SDB / Optical at the top, so a legacy user tag that
// repeats one of those words is duplicate noise (and worse — when
// the legacy tag *disagrees* with the derived modality, it makes
// the conflict visually loud, e.g. EarthwormTesticle has user tag
// "TEM" while the derived chip says "SDB"). Hide modality-keyword
// tags from the card-only chip row; the underlying data is unchanged
// and the full tag list still renders in the detail dialog so users
// can see and remove the legacy tag if they want to.
const MODALITY_TAG_KEYWORDS = new Set([
  "sem",
  "tem",
  "sdb",
  "optical",
  "stem",
  "stem-bf",
  "stem-haadf",
]);

function filterDisplayTags(tags: string[]): string[] {
  return tags.filter((t) => !MODALITY_TAG_KEYWORDS.has(t.toLowerCase()));
}

// ── Section header — bold label + left accent bar ─────────────

function SectionHeader({
  label,
  count,
  loading,
  solid = false,
}: {
  label: string;
  count: number;
  loading: boolean;
  solid?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 2,
        ...(solid && {
          bgcolor: "action.selected",
          borderRadius: 1,
          px: 2,
          py: 1.25,
        }),
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 22,
          bgcolor: "primary.main",
          borderRadius: "2px",
          flexShrink: 0,
        }}
      />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
        {label}
      </Typography>
      {!loading && (
        <Chip
          label={count}
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: "0.6875rem", ml: 0.25 }}
        />
      )}
      {loading && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
    </Box>
  );
}

// ── Thumbnail component ───────────────────────────────────────

interface ThumbnailProps {
  imageId: number;
  imageName: string;
  blobCache: React.MutableRefObject<Map<number, string>>;
  onClick: () => void;
}

function Thumbnail({ imageId, imageName, blobCache, onClick }: ThumbnailProps) {
  const [src, setSrc] = useState<string | null>(
    blobCache.current.get(imageId) ?? null
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (src || failed) return;
    let cancelled = false;
    imageApi
      .getThumbnailUrl(imageId)
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        blobCache.current.set(imageId, url);
        setSrc(url);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [imageId, src, failed]);

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: "4/3",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        overflow: "hidden",
        "&:hover": { opacity: 0.85 },
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      aria-label={`View ${imageName}`}
    >
      {src ? (
        <Box component="img" src={src} alt={imageName}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : failed ? (
        <ImageOutlinedIcon sx={{ fontSize: 40, color: "error.light" }} />
      ) : (
        <CircularProgress size={24} sx={{ color: "text.disabled" }} />
      )}
    </Box>
  );
}

// ── Image card — My Images section ────────────────────────────

interface MyImageCardProps {
  image: UserImage;
  blobCache: React.MutableRefObject<Map<number, string>>;
  onView: (image: UserImage, blobUrl: string | null) => void;
  onDelete: (id: number) => void;
  onToggleShare: (id: number, currentValue: boolean) => void;
  onImageChanged: (updated: UserImage) => void;
}

function MyImageCard({ image, blobCache, onView, onDelete, onToggleShare, onImageChanged }: MyImageCardProps) {
  function handleView() { onView(image, blobCache.current.get(image.id) ?? null); }
  const [detailsOpen, setDetailsOpen] = useState(false);

  const tagList = image.tags
    ? image.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];
  // Card-display only — modality words are surfaced by the derived
  // chip row above; the detail dialog still shows the full tag list.
  const displayTagList = filterDisplayTags(tagList);

  return (
    <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
      borderRadius: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Thumbnail imageId={image.id} imageName={image.name} blobCache={blobCache} onClick={handleView} />
      <Box sx={{ p: 1.25, flex: 1 }}>
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: "break-all", lineHeight: 1.3 }}>
            {image.name}
          </Typography>
        </Box>
        {image.derived_tags && (
          <Box sx={{ mb: 0.75 }}>
            <DerivedTagChips
              derived={image.derived_tags}
              metadata={image.acquisition_metadata}
              compact
            />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary"
          sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>
          {image.description}
        </Typography>
        <Box sx={{ mt: 0.75 }}>
          <MetadataHoverSummary
            metadata={image.acquisition_metadata}
            source={image.metadata_source}
          />
        </Box>
        {displayTagList.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
            {displayTagList.slice(0, 3).map((tag) => (
              <Chip key={tag} label={tag} size="small" sx={{ height: 16, fontSize: "0.5625rem" }} />
            ))}
            {displayTagList.length > 3 && (
              <Typography variant="caption" color="text.disabled">+{displayTagList.length - 3}</Typography>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ px: 1, pb: 0.75, pt: 0.75, display: "flex", alignItems: "center", gap: 0.25,
        borderTop: "1px solid", borderColor: "divider" }}>
        <Tooltip title="Image details">
          <IconButton size="small" onClick={() => setDetailsOpen(true)} aria-label="View image details">
            <InfoOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="View fullscreen">
          <IconButton size="small" onClick={handleView} aria-label="View image fullscreen">
            <FullscreenOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Delete image">
          <IconButton size="small" onClick={() => onDelete(image.id)} aria-label="Delete image" sx={{ color: "error.main" }}>
            <DeleteOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={image.is_global ? "Unshare" : "Share with community"}>
          <IconButton size="small" onClick={() => onToggleShare(image.id, image.is_global)}
            aria-label={image.is_global ? "Unshare image" : "Share image"}
            color={image.is_global ? "primary" : "default"}>
            {image.is_global
              ? <LinkOffOutlinedIcon sx={{ fontSize: 15 }} />
              : <ShareOutlinedIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
      </Box>
      <MetadataViewerDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        imageId={image.id}
        imageName={image.name}
        imageDescription={image.description}
        imageTags={image.tags}
        metadata={image.acquisition_metadata ?? null}
        source={image.metadata_source ?? "none"}
        onImageChanged={onImageChanged}
      />
    </Box>
  );
}

// ── Image card — Shared section ───────────────────────────────

interface SharedImageCardProps {
  image: LibraryImage;
  blobCache: React.MutableRefObject<Map<number, string>>;
  onView: (image: LibraryImage, blobUrl: string | null) => void;
  onSave: (id: number) => Promise<void>;
  isOwner: boolean;
  alreadySaved: boolean;
}

function SharedImageCard({ image, blobCache, onView, onSave, isOwner, alreadySaved }: SharedImageCardProps) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(alreadySaved);
  const [detailsOpen, setDetailsOpen] = useState(false);

  async function handleSave() {
    if (saving || saved) return;
    setSaving(true);
    try { await onSave(image.id); setSaved(true); }
    finally { setSaving(false); }
  }

  function handleView() { onView(image, blobCache.current.get(image.id) ?? null); }

  const tagList = image.tags
    ? image.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];
  // Card-display only — modality words are surfaced by the derived
  // chip row above; the detail dialog still shows the full tag list.
  const displayTagList = filterDisplayTags(tagList);

  return (
    <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
      borderRadius: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Thumbnail imageId={image.id} imageName={image.name} blobCache={blobCache} onClick={handleView} />
      <Box sx={{ p: 1.25, flex: 1 }}>
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: "break-all", lineHeight: 1.3 }}>
            {image.name}
          </Typography>
        </Box>
        {image.derived_tags && (
          <Box sx={{ mb: 0.75 }}>
            <DerivedTagChips
              derived={image.derived_tags}
              metadata={image.acquisition_metadata}
              compact
            />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary"
          sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>
          {image.description}
        </Typography>
        <Box sx={{ mt: 0.75 }}>
          <MetadataHoverSummary
            metadata={image.acquisition_metadata}
            source={image.metadata_source}
          />
        </Box>
        {displayTagList.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
            {displayTagList.slice(0, 3).map((tag) => (
              <Chip key={tag} label={tag} size="small" sx={{ height: 16, fontSize: "0.5625rem" }} />
            ))}
            {displayTagList.length > 3 && (
              <Typography variant="caption" color="text.disabled">+{displayTagList.length - 3}</Typography>
            )}
          </Box>
        )}
        {image.uploader_name && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
            <PersonOutlinedIcon sx={{ fontSize: 12, color: "text.disabled" }} />
            <Typography variant="caption" color="text.disabled">{image.uploader_name}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ px: 1, pb: 0.75, pt: 0.75, display: "flex", alignItems: "center", gap: 0.25,
        borderTop: "1px solid", borderColor: "divider" }}>
        <Tooltip title="Image details">
          <IconButton size="small" onClick={() => setDetailsOpen(true)} aria-label="View image details">
            <InfoOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="View fullscreen">
          <IconButton size="small" onClick={handleView} aria-label="View image fullscreen">
            <FullscreenOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {!isOwner && (
          <Tooltip title={saved ? "Already in your library" : "Save to My Images"}>
            <span>
              <Button size="small" variant="outlined"
                startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <BookmarkAddOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={handleSave} disabled={saving || saved}
                sx={{ fontSize: "0.65rem", py: 0.25, px: 0.75, textTransform: "none", minWidth: 0 }}>
                {saved ? "Saved" : "Save"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      <MetadataViewerDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        imageId={image.id}
        imageName={image.name}
        imageDescription={image.description}
        imageTags={image.tags}
        metadata={image.acquisition_metadata ?? null}
        source={image.metadata_source ?? "none"}
      />
    </Box>
  );
}

// ── Empty state ───────────────────────────────────────────────

function EmptyState({ message }: { message: string }) {
  return (
    <Box sx={{ p: 3, textAlign: "center", bgcolor: "background.paper", borderRadius: 1,
      border: "1px dashed", borderColor: "divider" }}>
      <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
  );
}

// ── Image grid ────────────────────────────────────────────────

const GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: 1.5,
};

// ── Filter chip row ───────────────────────────────────────────
//
// Filters live in the main component state and are passed through
// to imageApi.getMyImages / getSharedImages as query params on
// every fetch. Multi-select within a key (modality, magnification)
// OR-combine; different keys AND-combine. `calibrated` is a single
// boolean toggle. Server-side filtering matches Sam's contract
// (PROGRESS.md 2026-05-05).
//
// Visual model: outlined chips for inactive, filled primary for
// active — same toggle pattern used elsewhere on the dashboard
// (BacklogView tag chips). "Clear filters" is shown only when
// at least one filter is active.

const MODALITY_OPTIONS = ["SEM", "TEM", "SDB", "Optical"] as const;
const MAGNIFICATION_OPTIONS: { value: string; label: string }[] = [
  { value: "Low-mag", label: "Low" },
  { value: "Mid-mag", label: "Mid" },
  { value: "High-mag", label: "High" },
];

interface FilterState {
  modality: string[];
  magnification: string[];
  calibratedOnly: boolean;
}

const EMPTY_FILTERS: FilterState = {
  modality: [],
  magnification: [],
  calibratedOnly: false,
};

function filtersAreEmpty(f: FilterState): boolean {
  return (
    f.modality.length === 0 &&
    f.magnification.length === 0 &&
    !f.calibratedOnly
  );
}

function filtersToApi(f: FilterState): ImageListFilters {
  const out: ImageListFilters = {};
  if (f.modality.length) out.modality = f.modality;
  if (f.magnification.length) out.magnification = f.magnification;
  if (f.calibratedOnly) out.calibrated = true;
  return out;
}

interface FilterChipRowProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
}

function FilterChipRow({ filters, onChange }: FilterChipRowProps) {
  const toggleMulti = (key: "modality" | "magnification", value: string) => {
    const current = filters[key];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [key]: next });
  };
  const isActive = !filtersAreEmpty(filters);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        rowGap: 0.75,
        mb: 2,
        py: 1,
        px: 1.25,
        bgcolor: "action.hover",
        borderRadius: 1,
        border: "1px solid",
        borderColor: isActive ? "primary.main" : "divider",
      }}
    >
      {/* Group: Modality */}
      <FilterGroupLabel>Modality</FilterGroupLabel>
      {MODALITY_OPTIONS.map((opt) => {
        const active = filters.modality.includes(opt);
        return (
          <Chip
            key={opt}
            label={opt}
            size="small"
            clickable
            onClick={() => toggleMulti("modality", opt)}
            color={active ? "primary" : "default"}
            variant={active ? "filled" : "outlined"}
            sx={{ height: 24, fontSize: "0.7rem", fontWeight: active ? 600 : 500 }}
          />
        );
      })}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

      {/* Group: Magnification */}
      <FilterGroupLabel>Magnification</FilterGroupLabel>
      {MAGNIFICATION_OPTIONS.map((opt) => {
        const active = filters.magnification.includes(opt.value);
        return (
          <Chip
            key={opt.value}
            label={opt.label}
            size="small"
            clickable
            onClick={() => toggleMulti("magnification", opt.value)}
            color={active ? "primary" : "default"}
            variant={active ? "filled" : "outlined"}
            sx={{ height: 24, fontSize: "0.7rem", fontWeight: active ? 600 : 500 }}
          />
        );
      })}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

      {/* Single toggle: calibrated only */}
      <Chip
        label="Calibrated only"
        icon={<StraightenOutlinedIcon sx={{ fontSize: 13 }} />}
        size="small"
        clickable
        onClick={() => onChange({ ...filters, calibratedOnly: !filters.calibratedOnly })}
        color={filters.calibratedOnly ? "success" : "default"}
        variant={filters.calibratedOnly ? "filled" : "outlined"}
        sx={{
          height: 24,
          fontSize: "0.7rem",
          fontWeight: filters.calibratedOnly ? 600 : 500,
          "& .MuiChip-icon": {
            color: filters.calibratedOnly ? "inherit" : "text.secondary",
          },
        }}
      />

      <Box sx={{ flex: 1 }} />

      {isActive && (
        <Tooltip title="Clear all filters">
          <Button
            size="small"
            variant="text"
            startIcon={<FilterAltOffOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => onChange(EMPTY_FILTERS)}
            sx={{ fontSize: "0.7rem", py: 0.25, textTransform: "none", flexShrink: 0 }}
          >
            Clear filters
          </Button>
        </Tooltip>
      )}
    </Box>
  );
}

function FilterGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontSize: "0.65rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "text.secondary",
        mr: 0.25,
      }}
    >
      {children}
    </Typography>
  );
}

// ── Main component ────────────────────────────────────────────

export function ImageLibraryTab() {
  const authUser = getAuthUser();

  const [myImages, setMyImages] = useState<UserImage[]>([]);
  const [sharedImages, setSharedImages] = useState<LibraryImage[]>([]);
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const [loading, setLoading] = useState({ my: false, shared: false });
  const [errors, setErrors] = useState<{ my: string | null; shared: string | null }>({
    my: null, shared: null,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; severity: "success" | "info" | "error";
  }>({ open: false, message: "", severity: "success" });

  const blobCache = useRef<Map<number, string>>(new Map());
  const [viewingImage, setViewingImage] = useState<{
    image: UserImage | LibraryImage; blobUrl: string | null;
  } | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // ── Filtered views ──────────────────────────────────────────
  // Server-side filtering handles derived-tag filters; client-side
  // search narrows further by name/description against the current
  // server result.

  const q = searchQuery.trim().toLowerCase();

  const filteredMyImages = useMemo(() => {
    if (!q) return myImages;
    return myImages.filter(
      (img) => img.name.toLowerCase().includes(q) || img.description.toLowerCase().includes(q)
    );
  }, [myImages, q]);

  const filteredSharedImages = useMemo(() => {
    if (!q) return sharedImages;
    return sharedImages.filter(
      (img) => img.name.toLowerCase().includes(q) || img.description.toLowerCase().includes(q)
    );
  }, [sharedImages, q]);

  // ── Fetch helpers ───────────────────────────────────────────

  const fetchMyImages = useCallback(async (apiFilters: ImageListFilters) => {
    setLoading((prev) => ({ ...prev, my: true }));
    setErrors((prev) => ({ ...prev, my: null }));
    try { setMyImages(await imageApi.getMyImages(apiFilters)); }
    catch (err) { setErrors((prev) => ({ ...prev, my: err instanceof Error ? err.message : "Failed to load your images" })); }
    finally { setLoading((prev) => ({ ...prev, my: false })); }
  }, []);

  const fetchSharedImages = useCallback(async (apiFilters: ImageListFilters) => {
    setLoading((prev) => ({ ...prev, shared: true }));
    setErrors((prev) => ({ ...prev, shared: null }));
    try { setSharedImages(await imageApi.getSharedImages(apiFilters)); }
    catch (err) { setErrors((prev) => ({ ...prev, shared: err instanceof Error ? err.message : "Failed to load shared images" })); }
    finally { setLoading((prev) => ({ ...prev, shared: false })); }
  }, []);

  // Refetch whenever filter state changes — server applies the filters.
  useEffect(() => {
    const apiFilters = filtersToApi(filters);
    void fetchMyImages(apiFilters);
    void fetchSharedImages(apiFilters);
  }, [fetchMyImages, fetchSharedImages, filters]);

  useEffect(() => {
    const cache = blobCache.current;
    return () => { cache.forEach((url) => URL.revokeObjectURL(url)); cache.clear(); };
  }, []);

  // ── Actions ─────────────────────────────────────────────────

  function handleRefresh() {
    const apiFilters = filtersToApi(filters);
    void fetchMyImages(apiFilters);
    void fetchSharedImages(apiFilters);
  }

  function showSnackbar(message: string, severity: "success" | "info" | "error" = "success") {
    setSnackbar({ open: true, message, severity });
  }

  async function handleDelete(id: number) {
    try {
      await imageApi.deleteImage(id);
      const url = blobCache.current.get(id);
      if (url) { URL.revokeObjectURL(url); blobCache.current.delete(id); }
      setMyImages((prev) => prev.filter((img) => img.id !== id));
    } catch (err) {
      setErrors((prev) => ({ ...prev, my: err instanceof Error ? err.message : "Failed to delete image" }));
    }
  }

  async function handleToggleShare(id: number, currentValue: boolean) {
    try {
      const updated = await imageApi.setVisibility(id, !currentValue);
      setMyImages((prev) => prev.map((img) => (img.id === id ? updated : img)));
      void fetchSharedImages(filtersToApi(filters));
    } catch (err) {
      setErrors((prev) => ({ ...prev, my: err instanceof Error ? err.message : "Failed to update visibility" }));
    }
  }

  async function handleSaveToLibrary(imageId: number): Promise<void> {
    try {
      await imageApi.saveToLibrary(imageId);
      setSavedIds((prev) => new Set(prev).add(imageId));
      showSnackbar("Saved to My Images.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      showSnackbar(msg.includes("already have a copy") ? "Already in your library." : "Failed to save image.",
        msg.includes("already have a copy") ? "info" : "error");
      throw err;
    }
  }

  function handleUploadSuccess(image: UserImage) { setMyImages((prev) => [image, ...prev]); }

  function handleMyImageChanged(updated: UserImage) {
    setMyImages((prev) => prev.map((img) => (img.id === updated.id ? { ...img, ...updated } : img)));
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setIsDragOver(false); setShowUploadModal(true);
  }

  function handleView(image: UserImage | LibraryImage, blobUrl: string | null) {
    setViewingImage({ image, blobUrl });
  }

  // ── Render ──────────────────────────────────────────────────

  return (
    <Box
      sx={{ height: "100%", overflow: "auto" }}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false); }}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <Box sx={{ position: "fixed", inset: 0, bgcolor: "rgba(79,195,247,0.12)", border: "3px dashed",
          borderColor: "primary.main", zIndex: 1000, display: "flex", alignItems: "center",
          justifyContent: "center", pointerEvents: "none" }}>
          <Typography variant="h2" color="primary.main">Drop image to upload</Typography>
        </Box>
      )}

      <Box sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>

        {/* Search + toolbar row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <OutlinedInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or description…"
            size="small"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            }
            endAdornment={
              searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")} edge="end" aria-label="Clear search">
                    <ClearOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
            sx={{ fontSize: "0.875rem" }}
          />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh} aria-label="Refresh images">
              <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => setShowUploadModal(true)}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Upload
          </Button>
        </Box>

        {/* ── Filter chip row (derived tags) ── */}
        <FilterChipRow filters={filters} onChange={setFilters} />

        {/* ── My Images ── */}
        <SectionHeader
          label="My Images"
          count={filteredMyImages.length}
          loading={loading.my}
        />

        {errors.my && (
          <Alert severity="error" onClose={() => setErrors((prev) => ({ ...prev, my: null }))} sx={{ mb: 1.5 }}>
            {errors.my}
          </Alert>
        )}

        {loading.my ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredMyImages.length === 0 ? (
          <EmptyState message={
            q
              ? `No images matching "${searchQuery}" in your library.`
              : !filtersAreEmpty(filters)
                ? "No images in your library match the current filters."
                : "No uploaded images yet. Click Upload or drag an image onto this page."
          } />
        ) : (
          <Box sx={GRID_SX}>
            {filteredMyImages.map((img) => (
              <MyImageCard key={img.id} image={img} blobCache={blobCache}
                onView={handleView} onDelete={handleDelete} onToggleShare={handleToggleShare}
                onImageChanged={handleMyImageChanged} />
            ))}
          </Box>
        )}

        {/* ── Shared Library ── */}
        <Divider sx={{ my: 4 }} />

        <SectionHeader
          label="Shared Library"
          count={filteredSharedImages.length}
          loading={loading.shared}
          solid
        />

        {errors.shared && (
          <Alert severity="error" onClose={() => setErrors((prev) => ({ ...prev, shared: null }))} sx={{ mb: 1.5 }}>
            {errors.shared}
          </Alert>
        )}

        {loading.shared ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredSharedImages.length === 0 ? (
          <EmptyState message={
            q
              ? `No shared images matching "${searchQuery}".`
              : !filtersAreEmpty(filters)
                ? "No shared images match the current filters."
                : "No images have been shared with the community yet."
          } />
        ) : (
          <Box sx={GRID_SX}>
            {filteredSharedImages.map((img) => (
              <SharedImageCard key={img.id} image={img} blobCache={blobCache}
                onView={handleView} onSave={handleSaveToLibrary}
                isOwner={authUser !== null && img.user_id === authUser.id}
                alreadySaved={savedIds.has(img.id)} />
            ))}
          </Box>
        )}
      </Box>

      <FullscreenImageModal
        open={viewingImage !== null}
        onClose={() => setViewingImage(null)}
        imageName={viewingImage?.image.name}
        imageId={viewingImage?.image.id}
        blobUrl={viewingImage?.blobUrl ?? undefined}
      />
      <UploadImageModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadSuccess}
      />

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
